import React, { useState, useEffect, useMemo } from 'react';
import { getDashboardStats, getProspects, downloadExport, getComplianceReport } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
    Users, 
    FileText, 
    TrendingUp, 
    Calendar,
    HardHat,
    Star,
    ChevronDown,
    Download,
    AlertTriangle,
    FileWarning,
    Receipt,
    Wrench,
    CalendarRange,
    X
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    Tooltip, 
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    CartesianGrid,
    Legend
} from 'recharts';
import { toast } from 'sonner';
import { format as fmtDate, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, parseISO, isValid } from 'date-fns';

// Resolve a period preset (or 'custom') to a {start_date, end_date} pair as YYYY-MM-DD
const periodToRange = (period, customStart, customEnd) => {
    const today = new Date();
    let start, end;
    switch (period) {
        case 'week':
            start = startOfWeek(today, { weekStartsOn: 1 });
            end = endOfWeek(today, { weekStartsOn: 1 });
            break;
        case 'quarter':
            start = startOfQuarter(today);
            end = endOfQuarter(today);
            break;
        case 'year':
            start = startOfYear(today);
            end = endOfYear(today);
            break;
        case 'custom': {
            const s = customStart ? parseISO(customStart) : null;
            const e = customEnd ? parseISO(customEnd) : null;
            return {
                start_date: s && isValid(s) ? fmtDate(s, 'yyyy-MM-dd') : '',
                end_date: e && isValid(e) ? fmtDate(e, 'yyyy-MM-dd') : ''
            };
        }
        case 'month':
        default:
            start = startOfMonth(today);
            end = endOfMonth(today);
            break;
    }
    return {
        start_date: fmtDate(start, 'yyyy-MM-dd'),
        end_date: fmtDate(end, 'yyyy-MM-dd')
    };
};

export const ReportingPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [prospects, setProspects] = useState([]);
    const [compliance, setCompliance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const isAdmin = user?.role === 'admin';

    // Resolve the active range based on the period selection
    const range = useMemo(
        () => periodToRange(period, customStart, customEnd),
        [period, customStart, customEnd]
    );

    const rangeLabel = useMemo(() => {
        if (!range.start_date && !range.end_date) return 'Toutes les données';
        const fmt = (d) => {
            const dt = d ? parseISO(d) : null;
            return dt && isValid(dt) ? fmtDate(dt, 'dd MMM yyyy') : '—';
        };
        return `${fmt(range.start_date)} → ${fmt(range.end_date)}`;
    }, [range]);

    useEffect(() => {
        const r = periodToRange(period, customStart, customEnd);
        if (period === 'custom' && (!r.start_date || !r.end_date)) return;
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            try {
                const params = {};
                if (r.start_date) params.start_date = r.start_date;
                if (r.end_date) params.end_date = r.end_date;
                const [statsRes, prospectsRes] = await Promise.all([
                    getDashboardStats(params),
                    getProspects()
                ]);
                if (cancelled) return;
                setStats(statsRes.data);
                setProspects(prospectsRes.data);
                if (user?.role === 'admin' || user?.role === 'manager') {
                    try {
                        const c = await getComplianceReport();
                        if (!cancelled) setCompliance(c.data);
                    } catch {
                        if (!cancelled) setCompliance(null);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                if (!cancelled) toast.error('Erreur lors du chargement');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [period, customStart, customEnd, user?.role]);

    if (loading || !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // Transform data for charts
    const pipelineData = [
        { name: 'Nouveaux', value: (stats.pipeline_counts?.nouveau_prospect || 0) + (stats.pipeline_counts?.a_qualifier || 0) },
        { name: 'RDV', value: (stats.pipeline_counts?.rdv_a_planifier || 0) + (stats.pipeline_counts?.rdv_planifie || 0) },
        { name: 'Devis', value: (stats.pipeline_counts?.devis_a_faire || 0) + (stats.pipeline_counts?.devis_envoye || 0) + (stats.pipeline_counts?.relance_a_faire || 0) },
        { name: 'Signés', value: stats.pipeline_counts?.devis_accepte || 0 },
        { name: 'Chantiers', value: (stats.pipeline_counts?.chantier_planifie || 0) + (stats.pipeline_counts?.chantier_en_cours || 0) },
        { name: 'Terminés', value: (stats.pipeline_counts?.chantier_termine || 0) + (stats.pipeline_counts?.reception_effectuee || 0) + (stats.pipeline_counts?.dossier_clos || 0) }
    ];

    const sourcesData = stats.sources ? Object.entries(stats.sources).map(([name, value]) => ({
        name: name || 'Autre',
        value
    })).sort((a, b) => b.value - a.value) : [];

    const COLORS = ['#E85A24', '#16A34A', '#D97706', '#0284C7', '#7C3AED', '#DC2626'];

    // Calculate conversion rates
    const totalProspects = stats.totals?.prospects + stats.totals?.clients || 0;
    const totalClients = stats.totals?.clients || 0;
    const conversionRate = totalProspects > 0 ? Math.round((totalClients / totalProspects) * 100) : 0;

    // Transformation funnel
    const funnelData = [
        { stage: 'Prospects', count: totalProspects, rate: 100 },
        { stage: 'RDV', count: pipelineData[1].value + pipelineData[2].value + pipelineData[3].value + pipelineData[4].value + pipelineData[5].value, rate: 0 },
        { stage: 'Devis', count: pipelineData[2].value + pipelineData[3].value + pipelineData[4].value + pipelineData[5].value, rate: 0 },
        { stage: 'Signés', count: pipelineData[3].value + pipelineData[4].value + pipelineData[5].value, rate: 0 },
        { stage: 'Terminés', count: pipelineData[5].value, rate: 0 }
    ];
    
    // Calculate rates
    funnelData.forEach((item, idx) => {
        if (idx > 0 && funnelData[idx - 1].count > 0) {
            item.rate = Math.round((item.count / funnelData[0].count) * 100);
        }
    });

    return (
        <div className="space-y-6" data-testid="reporting-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-[#0A0A0A] tracking-tight">Reporting</h1>
                    <p className="text-[#52525B] mt-1">Analyse de vos performances commerciales</p>
                    <p className="text-xs text-[#52525B] mt-1 flex items-center gap-1" data-testid="reporting-range-label">
                        <CalendarRange className="w-3.5 h-3.5" /> Période active : <span className="font-semibold">{rangeLabel}</span>
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <Select value={period} onValueChange={(v) => {
                        setPeriod(v);
                        if (v !== 'custom') { setCustomStart(''); setCustomEnd(''); }
                    }}>
                        <SelectTrigger className="w-full sm:w-[180px] rounded-sm" data-testid="reporting-period-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="week">Cette semaine</SelectItem>
                            <SelectItem value="month">Ce mois</SelectItem>
                            <SelectItem value="quarter">Ce trimestre</SelectItem>
                            <SelectItem value="year">Cette année</SelectItem>
                            <SelectItem value="custom">Personnalisé...</SelectItem>
                        </SelectContent>
                    </Select>
                    {period === 'custom' && (
                        <div className="flex items-center gap-2 bg-white border border-[#E4E4E7] rounded-sm p-2" data-testid="custom-range-picker">
                            <Input
                                type="date"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                className="h-9 rounded-sm w-[150px]"
                                max={customEnd || undefined}
                                data-testid="custom-start-input"
                            />
                            <span className="text-[#52525B]">→</span>
                            <Input
                                type="date"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                className="h-9 rounded-sm w-[150px]"
                                min={customStart || undefined}
                                data-testid="custom-end-input"
                            />
                            {(customStart || customEnd) && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                                    className="h-9 w-9"
                                    title="Effacer les dates"
                                    data-testid="custom-range-clear"
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Exports (Admin only) */}
            {isAdmin && (
                <div className="bg-white border border-[#E4E4E7] p-6 rounded-sm" data-testid="exports-section">
                    <h3 className="font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
                        <Download className="w-5 h-5 text-[#E85A24]" /> Exports
                    </h3>
                    <div className="mb-4 p-4 border-2 border-[#E85A24]/30 bg-[#FFF7ED] rounded-sm">
                        <p className="font-semibold mb-1 flex items-center gap-2">
                            <Download className="w-4 h-4 text-[#E85A24]" /> Export global tous projets
                        </p>
                        <p className="text-sm text-[#52525B] mb-3">
                            Excel multi-feuilles : Vue d&apos;ensemble, Devis, Factures, Coûts, PVs & Réserves, Litiges, Notes. Toutes les feuilles sont liées par le <code className="bg-white px-1 rounded">Dossier ID</code> pour croiser les données facilement.
                        </p>
                        <button
                            onClick={() => window.open('/api/exports/projects', '_blank')}
                            className="px-4 py-2 text-sm bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm font-medium"
                            data-testid="export-projects-xlsx"
                        >
                            <Download className="w-4 h-4 inline mr-1" /> Télécharger l&apos;Excel complet
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 border border-[#E4E4E7] rounded-sm">
                            <p className="font-semibold mb-2">RDV pris</p>
                            <p className="text-sm text-[#52525B] mb-3">Tous les rendez-vous planifiés avec les prospects concernés sur la période sélectionnée</p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => downloadExport('rdvs', 'csv', range)}
                                    className="px-3 py-2 text-sm border border-[#E4E4E7] hover:bg-[#F4F4F5] rounded-sm"
                                    data-testid="export-rdvs-csv"
                                >
                                    <Download className="w-4 h-4 inline mr-1" /> CSV
                                </button>
                                <button
                                    onClick={() => downloadExport('rdvs', 'xlsx', range)}
                                    className="px-3 py-2 text-sm border border-[#E4E4E7] hover:bg-[#F4F4F5] rounded-sm"
                                    data-testid="export-rdvs-xlsx"
                                >
                                    <Download className="w-4 h-4 inline mr-1" /> Excel
                                </button>
                            </div>
                        </div>
                        <div className="p-4 border border-[#E4E4E7] rounded-sm">
                            <p className="font-semibold mb-2">Projets en relance</p>
                            <p className="text-sm text-[#52525B] mb-3">Dossiers actuellement au statut « relance » sur la période sélectionnée</p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => downloadExport('relances', 'csv', range)}
                                    className="px-3 py-2 text-sm border border-[#E4E4E7] hover:bg-[#F4F4F5] rounded-sm"
                                    data-testid="export-relances-csv"
                                >
                                    <Download className="w-4 h-4 inline mr-1" /> CSV
                                </button>
                                <button
                                    onClick={() => downloadExport('relances', 'xlsx', range)}
                                    className="px-3 py-2 text-sm border border-[#E4E4E7] hover:bg-[#F4F4F5] rounded-sm"
                                    data-testid="export-relances-xlsx"
                                >
                                    <Download className="w-4 h-4 inline mr-1" /> Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Compliance Reports (admin/manager only) */}
            {compliance && (
                <div className="bg-white border border-[#E4E4E7] p-4 sm:p-6 rounded-sm" data-testid="compliance-section">
                    <h3 className="font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
                        <FileWarning className="w-5 h-5 text-[#DC2626]" /> Rapports de conformité
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                        {/* TVA Missing */}
                        <details className="border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="compliance-tva">
                            <summary className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-[#FAFAFA] list-none">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-[#D97706]/10 rounded-sm shrink-0">
                                        <Receipt className="w-5 h-5 text-[#D97706]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm">Attestations TVA manquantes</p>
                                        <p className="text-xs text-[#52525B]">Dossiers signés sans pièce TVA</p>
                                    </div>
                                </div>
                                <span className={`text-xl font-bold shrink-0 ${compliance.tva_missing_count > 0 ? 'text-[#DC2626]' : 'text-[#16A34A]'}`}>
                                    {compliance.tva_missing_count}
                                </span>
                            </summary>
                            <div className="border-t border-[#E4E4E7] divide-y divide-[#E4E4E7] max-h-72 overflow-y-auto">
                                {compliance.tva_missing.length === 0 ? (
                                    <p className="p-3 text-xs text-[#16A34A]">Aucun dossier en défaut ✓</p>
                                ) : compliance.tva_missing.map((d) => (
                                    <button
                                        key={d.dossier_id}
                                        onClick={() => navigate(`/dossiers/${d.dossier_id}`)}
                                        className="w-full text-left p-3 hover:bg-[#FAFAFA] text-xs"
                                        data-testid={`tva-missing-${d.dossier_id}`}
                                    >
                                        <p className="font-medium text-[#0A0A0A]">{d.titre}</p>
                                        <p className="text-[#52525B]">{d.prospect_nom} · {Number(d.montant_devis).toLocaleString('fr-FR')} € HT</p>
                                    </button>
                                ))}
                            </div>
                        </details>

                        {/* Open reserves */}
                        <details className="border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="compliance-reserves">
                            <summary className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-[#FAFAFA] list-none">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-[#DC2626]/10 rounded-sm shrink-0">
                                        <Wrench className="w-5 h-5 text-[#DC2626]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm">Réserves non levées</p>
                                        <p className="text-xs text-[#52525B]">PV avec points en attente</p>
                                    </div>
                                </div>
                                <span className={`text-xl font-bold shrink-0 ${compliance.reserves_open_count > 0 ? 'text-[#DC2626]' : 'text-[#16A34A]'}`}>
                                    {compliance.reserves_open_count}
                                </span>
                            </summary>
                            <div className="border-t border-[#E4E4E7] divide-y divide-[#E4E4E7] max-h-72 overflow-y-auto">
                                {compliance.reserves_open.length === 0 ? (
                                    <p className="p-3 text-xs text-[#16A34A]">Toutes les réserves sont levées ✓</p>
                                ) : compliance.reserves_open.map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => r.dossier_id && navigate(`/dossiers/${r.dossier_id}`)}
                                        className="w-full text-left p-3 hover:bg-[#FAFAFA] text-xs"
                                        data-testid={`reserve-open-${r.id}`}
                                    >
                                        <p className="font-medium text-[#0A0A0A] line-clamp-2">{r.description}</p>
                                        <p className="text-[#52525B] mt-0.5">
                                            {r.dossier_titre}{r.prospect_nom ? ` · ${r.prospect_nom}` : ''}
                                            {r.age_days > 0 && <span className={`ml-1 font-medium ${r.age_days > 30 ? 'text-[#DC2626]' : 'text-[#D97706]'}`}>· {r.age_days}j</span>}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </details>

                        {/* Unbilled completed */}
                        <details className="border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="compliance-unbilled">
                            <summary className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-[#FAFAFA] list-none">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-[#7C3AED]/10 rounded-sm shrink-0">
                                        <AlertTriangle className="w-5 h-5 text-[#7C3AED]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm">Chantiers terminés sous-facturés</p>
                                        <p className="text-xs text-[#52525B]">&lt; 95% du devis facturé</p>
                                    </div>
                                </div>
                                <span className={`text-xl font-bold shrink-0 ${compliance.unbilled_completed_count > 0 ? 'text-[#DC2626]' : 'text-[#16A34A]'}`}>
                                    {compliance.unbilled_completed_count}
                                </span>
                            </summary>
                            <div className="border-t border-[#E4E4E7] divide-y divide-[#E4E4E7] max-h-72 overflow-y-auto">
                                {compliance.unbilled_completed.length === 0 ? (
                                    <p className="p-3 text-xs text-[#16A34A]">Toutes les factures à jour ✓</p>
                                ) : compliance.unbilled_completed.map((d) => (
                                    <button
                                        key={d.dossier_id}
                                        onClick={() => navigate(`/dossiers/${d.dossier_id}`)}
                                        className="w-full text-left p-3 hover:bg-[#FAFAFA] text-xs"
                                        data-testid={`unbilled-${d.dossier_id}`}
                                    >
                                        <p className="font-medium text-[#0A0A0A]">{d.titre}</p>
                                        <p className="text-[#52525B]">{d.prospect_nom}</p>
                                        <p className="text-[#DC2626] font-semibold mt-0.5">
                                            Reste à facturer : {Number(d.reste_a_facturer).toLocaleString('fr-FR')} €
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </details>
                    </div>
                </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-2 text-[#52525B] mb-2">
                        <Users className="w-5 h-5" />
                        <span className="field-label">Prospects</span>
                    </div>
                    <p className="text-3xl font-bold">{stats.totals?.prospects || 0}</p>
                    <p className="text-sm text-[#16A34A] mt-1">+{stats.monthly?.nouveaux_prospects || 0} ce mois</p>
                </div>
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-2 text-[#52525B] mb-2">
                        <FileText className="w-5 h-5" />
                        <span className="field-label">Devis envoyés</span>
                    </div>
                    <p className="text-3xl font-bold">{pipelineData[2].value + pipelineData[3].value}</p>
                </div>
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-2 text-[#52525B] mb-2">
                        <TrendingUp className="w-5 h-5" />
                        <span className="field-label">Taux transformation</span>
                    </div>
                    <p className="text-3xl font-bold text-[#E85A24]">{stats.taux_transformation}%</p>
                </div>
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-2 text-[#52525B] mb-2">
                        <HardHat className="w-5 h-5" />
                        <span className="field-label">Chantiers en cours</span>
                    </div>
                    <p className="text-3xl font-bold">{stats.chantiers?.en_cours || 0}</p>
                </div>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pipeline Chart */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-semibold text-[#0A0A0A] mb-4">Répartition du pipeline</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={pipelineData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#E85A24" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Sources Chart */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-semibold text-[#0A0A0A] mb-4">Sources d&apos;acquisition</h3>
                    {sourcesData.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={sourcesData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                        labelLine={false}
                                    >
                                        {sourcesData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-3 mt-4 justify-center">
                                {sourcesData.map((item, index) => (
                                    <div key={item.name} className="flex items-center gap-2 text-sm">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                        <span>{item.name}: {item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="h-[250px] flex items-center justify-center text-[#52525B]">
                            Aucune donnée
                        </div>
                    )}
                </div>
            </div>

            {/* Conversion Funnel */}
            <div className="bg-white border border-[#E4E4E7] p-4 sm:p-6">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Entonnoir de conversion</h3>
                <div className="overflow-x-auto pb-2 -mx-1">
                    <div className="flex items-end justify-start sm:justify-center gap-3 sm:gap-4 h-56 sm:h-64 min-w-max px-2">
                        {funnelData.map((item, index) => (
                            <div key={item.stage} className="flex flex-col items-center shrink-0 w-20 sm:w-24">
                                <div
                                    className="w-full bg-[#E85A24] transition-all duration-300 rounded-t-sm flex items-end justify-center pb-2"
                                    style={{
                                        height: `${Math.max(20, item.rate * 2)}px`,
                                        opacity: 1 - (index * 0.15)
                                    }}
                                >
                                    <span className="text-white font-bold text-xs sm:text-sm">{item.count}</span>
                                </div>
                                <div className="mt-2 text-center w-full">
                                    <p className="font-medium text-xs sm:text-sm leading-tight">{item.stage}</p>
                                    <p className="text-xs text-[#52525B]">{item.rate}%</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Detailed Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Actions requises */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-semibold text-[#0A0A0A] mb-4">Actions requises</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-[#DC2626]/10 rounded-sm">
                            <span className="text-sm">Relances en retard</span>
                            <span className="font-bold text-[#DC2626]">{stats.actions_requises?.relances_en_retard || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#D97706]/10 rounded-sm">
                            <span className="text-sm">Devis à faire</span>
                            <span className="font-bold text-[#D97706]">{stats.actions_requises?.devis_a_faire || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#E85A24]/10 rounded-sm">
                            <span className="text-sm">RDV à planifier</span>
                            <span className="font-bold text-[#E85A24]">{stats.actions_requises?.rdv_a_planifier || 0}</span>
                        </div>
                    </div>
                </div>

                {/* Chantiers */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-semibold text-[#0A0A0A] mb-4">Chantiers</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-[#0284C7]/10 rounded-sm">
                            <span className="text-sm">Planifiés</span>
                            <span className="font-bold text-[#0284C7]">{stats.chantiers?.planifies || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#16A34A]/10 rounded-sm">
                            <span className="text-sm">En cours</span>
                            <span className="font-bold text-[#16A34A]">{stats.chantiers?.en_cours || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#7C3AED]/10 rounded-sm">
                            <span className="text-sm">Terminés (total)</span>
                            <span className="font-bold text-[#7C3AED]">{pipelineData[5].value}</span>
                        </div>
                    </div>
                </div>

                {/* Mois en cours */}
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <h3 className="font-semibold text-[#0A0A0A] mb-4">Ce mois</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-sm">
                            <span className="text-sm">Nouveaux prospects</span>
                            <span className="font-bold">{stats.monthly?.nouveaux_prospects || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-sm">
                            <span className="text-sm">Nouveaux dossiers</span>
                            <span className="font-bold">{stats.monthly?.nouveaux_dossiers || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-sm">
                            <span className="text-sm">Devis acceptés</span>
                            <span className="font-bold">{stats.monthly?.devis_acceptes || 0}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
